import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { MessageSquare, User, Users, Shield, CreditCard, Package, Mail, Handshake, Repeat, ShoppingBag, AlertCircle, Gavel, FileSignature, Sparkles, Wrench, Inbox, KeyRound, Zap, PhoneMissed, Gift, Palette, GitBranch, Plug, ChevronDown, Car, BookOpenText } from "lucide-react";
import AssistantKnowledgeSection from "../components/assistant/AssistantKnowledgeSection";
import { DealerProfileSection, BonusCatalogSection, MarketingSection, VehicleExportSection, VehicleImportSection, KoenigIntegrationSection, KoenigVehicleImportSection, CustomerImportSection, BuyRequestAcceptanceSection, MessagesNotificationCard, OffersNotificationCard, TradeinNotificationCard, BuyrequestsNotificationCard, AuctionsNotificationCard, FinanzenSection, WerkstattTeileZubehoerSection } from "../components/DealerSettings";
import { UploadPanel } from "../components/dealer/WarenwirtschaftSection";
import { CollapsibleCard } from "../components/dealer/_shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ImportModal from "./wms/ImportModal";
import BranchOverrideToggle from "../components/dealer/BranchOverrideToggle";
import MarketplaceSettingsSection from "../components/dealer/MarketplaceSettingsSection";

import EmployeesSection from "../components/EmployeesSection";
import CommissionConfigSection from "../components/CommissionConfigSection";
import SettingsToc from "../components/SettingsToc";
import { WarenwirtschaftSection } from "../components/dealer/WarenwirtschaftSection";
import OrgBranchSettings from "../components/dealer/OrgBranchSettings";
import { BrandingSection } from "../components/dealer/BrandingSection";
import FooterPreviewCard from "../components/dealer/FooterPreviewCard";
import DocumentSamplesCard from "../components/dealer/DocumentSamplesCard";
import EmailTemplatesSection from "../components/dealer/EmailTemplatesSection";
import OrgLockedSection from "../components/dealer/OrgLockedSection";
import SaveStatusIndicator from "../components/dealer/SaveStatusIndicator";
import { useAutosave } from "../hooks/useAutosave";
import UndoSnackbar from "../components/dealer/UndoSnackbar";
import FilialeInheritanceBanner from "../components/dealer/FilialeInheritanceBanner";
import DealerLegalDocs from "../components/dealer/DealerLegalDocs";
import PushSettingsSection from "../components/PushSettingsSection";

/**
 * DashboardSettings — der „Einstellungen"-Tab des Händler-Dashboards, ausgelagert
 * aus DashboardPage.jsx (Modularisierung P2, Phase D). Stateful (saveRef/onDirtyChange/useAuth).
 */
export default function Settings({ currentUser, onDirtyChange, saveRef }) {
    // Jun-2026 (User-Wunsch scholz): WMS-Zahnräder auch für Filial-/Verkaufsleiter
    // sichtbar, wenn sie mit dem Mitarbeiter-Konto (statt Händler-Konto) unterwegs sind.
    const isBranchLead = currentUser?.role === "zweiradverkaeufer"
        && ["filialleiter", "verkaufsleiter"].includes(currentUser?.employee_function);
    const { refresh } = useAuth();
    const navigate = useNavigate();
    // Dealer profile dirty is reported via callback from DealerProfileSection
    const [profileDirty, setProfileDirty] = useState(false);
    const [layoutOpen, setLayoutOpen] = useState(false);
    // Jun-2026 (User-Wunsch): Artikel-Import (CSV/König-Teile) lebt jetzt im
    // aufklappbaren „Warenbestand"-Bereich (Import/Export).
    const [articleImportOpen, setArticleImportOpen] = useState(false);
    // Phase 3.86 — Auto-Save-Indicator: zuletzt erfolgreicher Save (Timestamp ms).
    // Wird gesetzt nach saveMarket() und sobald profileDirty true → false fällt
    // (heuristisch = erfolgreicher Profil-Save oder Revert).
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const prevProfileDirtyRef = useRef(false);
    // Phase 3.86+ — Undo-Snackbar nach Marktplatz-Save (10 s)
    const [undoVisible, setUndoVisible] = useState(false);
    const [undoSnapshot, setUndoSnapshot] = useState(null); // {form, prefsPayload}
    // Phase 3.22 — Marktplatzeinstellungen ist als Ganzes ein-/ausklappbar
    // (wie Stammdaten + Unternehmensdaten). Default: eingeklappt.
    const [marketExpanded, setMarketExpanded] = useState(false);
    // Phase 3.22b — Marketing-Sektion (Banner + Code + QR + Sub-Sections) Top-Level kollabierbar.
    const [marketingExpanded, setMarketingExpanded] = useState(false);
    const [integrationsExpanded, setIntegrationsExpanded] = useState(false);
    // ---- Marktplatzeinstellungen: unified form state (no per-toggle save) ----
    const NOTIF_CATS = ["messages", "offers", "tradein", "buyrequests", "auctions"];
    const NOTIF_DEFAULTS = (u) => {
        const out = {};
        NOTIF_CATS.forEach((c) => {
            out[`notif_${c}_email_enabled`] = u[`notif_${c}_email_enabled`] === undefined ? true : !!u[`notif_${c}_email_enabled`];
            out[`notif_${c}_email_addr`] = u[`notif_${c}_email_addr`] || "";
            out[`notif_${c}_sms_enabled`] = !!u[`notif_${c}_sms_enabled`];
            out[`notif_${c}_sms_phone`] = u[`notif_${c}_sms_phone`] || "";
            out[`notif_${c}_whatsapp_enabled`] = !!u[`notif_${c}_whatsapp_enabled`];
        });
        return out;
    };
    const initialForm = {
        allow_offers: !!currentUser.allow_offers,
        // Direct chat opt-in — gates the Nachrichten notification category (Phase 3.19)
        direct_chat_enabled: !!currentUser.direct_chat_enabled,
        // Phase 3.22 — Automatischer Vertragsversand bei Reservierung/Kauf
        auto_contract_dispatch_enabled: !!currentUser.auto_contract_dispatch_enabled,
        reservation_hours: currentUser.reservation_hours ?? 48,
        financing_link_url: currentUser.financing_link_url || "",
        min_offer_pct: currentUser.min_offer_pct !== undefined && currentUser.min_offer_pct !== null
            ? String(currentUser.min_offer_pct)
            : "",
        auto_counter_offers: !!currentUser.auto_counter_offers,
        default_visibility_b2c: currentUser.default_visibility_b2c !== false,
        default_visibility_b2b: currentUser.default_visibility_b2b !== false,
        b2b_visible_dealer_ids: Array.isArray(currentUser.b2b_visible_dealer_ids) ? currentUser.b2b_visible_dealer_ids : [],
        instant_buy_enabled: !!currentUser.instant_buy_enabled,
        instant_buy_payment_enabled: !!currentUser.instant_buy_payment_enabled,
        instant_buy_deposit_eur: currentUser.instant_buy_deposit_eur != null ? String(currentUser.instant_buy_deposit_eur) : "",
        missed_call_alerts_enabled: !!currentUser.missed_call_alerts_enabled,
        missed_call_email_enabled: currentUser.missed_call_email_enabled !== false,
        missed_call_sms_enabled: !!currentUser.missed_call_sms_enabled,
        // Feb-2026 (User-Wunsch) — WhatsApp als Alternative zu SMS (gemockt)
        missed_call_whatsapp_enabled: !!currentUser.missed_call_whatsapp_enabled,
        whatsapp_enabled: !!currentUser.whatsapp_enabled,
        whatsapp_number: currentUser.whatsapp_number || "",
        allow_tradein: !!currentUser.allow_tradein,
        allow_chat: currentUser.allow_chat !== false,
        // Feb-2026 (User-Wunsch 24 May) — Probefahrten als Marktplatz-Einstellung
        allow_test_ride: !!currentUser.allow_test_ride,
        allow_auctions: currentUser.allow_auctions !== false,
        accept_buy_requests: !!currentUser.accept_buy_requests,
        terms_accepted: !!currentUser.buy_request_terms_accepted_at,
        program_pfand: !!currentUser.program_pfand,
        program_sharing: !!currentUser.program_sharing,
        program_bikevinci: !!currentUser.program_bikevinci,
        program_preistalk: !!currentUser.program_preistalk,
        // Sharing / Vermietung (Marktplatz) — Master + Mietzeiträume
        sharing_enabled: !!currentUser.sharing_enabled,
        sharing_period_week: !!currentUser.sharing_period_week,
        sharing_period_month: !!currentUser.sharing_period_month,
        sharing_period_season: !!currentUser.sharing_period_season,
        ...NOTIF_DEFAULTS(currentUser),
    };
    const [form, setForm] = useState(initialForm);
    const [savingMarket, setSavingMarket] = useState(false);
    const updateForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const dirtyMarket = (() => {
        const keys = Object.keys(initialForm);
        return keys.some((k) => form[k] !== initialForm[k]);
    })();

    // Propagate combined dirty state up to DashboardPage
    useEffect(() => {
        onDirtyChange?.(dirtyMarket || profileDirty);
    }, [dirtyMarket, profileDirty, onDirtyChange]);

    // Jun-2026 (User-Wunsch) — Deep-Link aus dem System-Cockpit: bei vorhandenem
    // URL-Hash (#settings-finanzen, #settings-2fa, #settings-market …) nach dem
    // Mount sanft zur entsprechenden Sektion scrollen.
    useEffect(() => {
        const hash = (window.location.hash || "").replace("#", "");
        if (!hash) return;
        if (hash === "settings-market") setMarketExpanded(true);
        if (hash === "settings-email-templates") setLayoutOpen(true);
        // Sektion kann (nach Reauth-Gate / großem Settings-Layout) verzögert
        // gerendert werden — daher mehrfach versuchen, bis das Element existiert.
        let tries = 0;
        const timers = [];
        const tryScroll = () => {
            const el = document.getElementById(hash);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            if (tries++ < 12) timers.push(setTimeout(tryScroll, 300));
        };
        timers.push(setTimeout(tryScroll, 300));
        return () => timers.forEach(clearTimeout);
    }, []);


    // Phase 3.86+ — Undo: vorherige User-Prefs wiederherstellen
    const performUndo = async () => {
        if (!undoSnapshot) return;
        setUndoVisible(false);
        setSavingMarket(true);
        try {
            const snap = undoSnapshot.prefs;
            const revertPayload = {
                allow_offers: !!snap.allow_offers,
                allow_chat: snap.allow_chat !== false,
                allow_test_ride: !!snap.allow_test_ride,
                allow_auctions: snap.allow_auctions !== false,
                allow_tradein: !!snap.allow_tradein,
                financing_link_url: snap.financing_link_url || "",
                min_offer_pct: snap.min_offer_pct,
                auto_counter_offers: !!snap.auto_counter_offers,
                instant_buy_enabled: !!snap.instant_buy_enabled,
            };
            await api.patch("/users/me/preferences", revertPayload);
            await refresh();
            setForm(undoSnapshot.form);
            toast.success("Rückgängig — vorherige Einstellungen wiederhergestellt");
            setLastSavedAt(Date.now());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rückgängig fehlgeschlagen");
        }
        setSavingMarket(false);
        setUndoSnapshot(null);
    };

    // Phase 3.86 — wenn profileDirty von true → false fällt, gilt das als Save-Signal
    // (DealerProfileSection ruft refresh()+toast intern auf, hat aber keinen
    // expliziten onSaved-Callback. Diese Heuristik ist robust für UX-Feedback).
    useEffect(() => {
        if (prevProfileDirtyRef.current && !profileDirty) {
            setLastSavedAt(Date.now());
        }
        prevProfileDirtyRef.current = profileDirty;
    }, [profileDirty]);

    // Expose a "save everything dirty" function to the parent via saveRef so the
    // tab-leave-confirm modal can offer "Speichern & Verlassen" without needing
    // to duplicate the logic. Profile save lives inside DealerProfileSection —
    // we trigger its existing button (data-testid="save-dealer-profile-btn") so
    // its internal validation + toast remain the single source of truth.
    useEffect(() => {
        if (!saveRef) return;
        saveRef.current = async () => {
            try {
                if (profileDirty) {
                    const btn = document.querySelector('[data-testid="save-dealer-profile-btn"]');
                    if (btn) {
                        btn.click();
                        // Give the in-flight PATCH a moment to land + refresh auth.
                        await new Promise((r) => setTimeout(r, 600));
                    }
                }
                if (dirtyMarket) {
                    await saveMarket();
                }
            } catch (_e) {
                // saveMarket already toasts on failure; swallow so callers can navigate anyway.
            }
        };
        return () => {
            if (saveRef.current && saveRef === saveRef) saveRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveRef, profileDirty, dirtyMarket]);

    const saveMarket = async ({ silent = false } = {}) => {
        // Jun-2026 (User-Wunsch): Autospeichern. `silent` = vom Autosave aufgerufen →
        // keine Erfolgs-Toasts/Undo-Snapshots, Validierungsfehler werfen (damit der
        // Autosave „nicht gespeichert" anzeigt) statt zu toasten.
        const fail = (msg) => {
            if (silent) throw new Error(msg);
            toast.error(msg);
            return false;
        };
        const prefsPayload = {
            allow_offers: form.allow_offers,
            allow_tradein: form.allow_tradein,
            allow_chat: form.allow_chat,
            allow_test_ride: form.allow_test_ride,
            allow_auctions: form.allow_auctions,
            instant_buy_enabled: !!form.instant_buy_enabled,
        };
        prefsPayload.instant_buy_payment_enabled = !!form.instant_buy_payment_enabled;
        if (form.instant_buy_payment_enabled) {
            const dv = (form.instant_buy_deposit_eur || "").trim();
            if (dv !== "") {
                const dn = Number(dv);
                if (!Number.isFinite(dn) || dn < 0) {
                    if (fail("Bitte einen gültigen Anzahlungsbetrag (€) eingeben") === false) return;
                }
                prefsPayload.instant_buy_deposit_eur = Math.round(dn * 100) / 100;
            } else {
                prefsPayload.instant_buy_deposit_eur = 0;
            }
        }
        prefsPayload.missed_call_alerts_enabled = !!form.missed_call_alerts_enabled;
        prefsPayload.missed_call_email_enabled = !!form.missed_call_email_enabled;
        prefsPayload.missed_call_sms_enabled = !!form.missed_call_sms_enabled;
        prefsPayload.missed_call_whatsapp_enabled = !!form.missed_call_whatsapp_enabled;
        if (form.allow_offers) {
            const v = (form.min_offer_pct || "").trim();
            if (v !== "") {
                const n = Number(v);
                if (!Number.isFinite(n) || n < 0 || n > 100) {
                    if (fail("Bitte einen Prozentwert zwischen 0 und 100 eingeben") === false) return;
                }
                prefsPayload.min_offer_pct = Math.round(n);
            }
            prefsPayload.auto_counter_offers = !!form.auto_counter_offers;
        }
        if (currentUser.role === "b2b") {
            if (form.accept_buy_requests && !form.terms_accepted) {
                if (fail("Bitte zuerst die Annahmebedingungen bestätigen.") === false) return;
            }
            prefsPayload.accept_buy_requests = form.accept_buy_requests;
            prefsPayload.program_pfand = form.program_pfand;
            prefsPayload.program_sharing = form.program_sharing;
            prefsPayload.program_bikevinci = form.program_bikevinci;
            prefsPayload.program_preistalk = form.program_preistalk;
            prefsPayload.sharing_enabled = !!form.sharing_enabled;
            prefsPayload.sharing_period_week = !!form.sharing_period_week;
            prefsPayload.sharing_period_month = !!form.sharing_period_month;
            prefsPayload.sharing_period_season = !!form.sharing_period_season;
        }
        setSavingMarket(true);
        try {
            if (currentUser.role === "b2b") {
                const dealerPayload = {
                    buy_request_terms_accepted_at: form.terms_accepted
                        ? (currentUser.buy_request_terms_accepted_at || new Date().toISOString())
                        : null,
                };
                NOTIF_CATS.forEach((c) => {
                    dealerPayload[`notif_${c}_email_enabled`] = form[`notif_${c}_email_enabled`];
                    dealerPayload[`notif_${c}_email_addr`] = form[`notif_${c}_email_addr`] || null;
                    dealerPayload[`notif_${c}_sms_enabled`] = form[`notif_${c}_sms_enabled`];
                    dealerPayload[`notif_${c}_sms_phone`] = form[`notif_${c}_sms_phone`] || null;
                    dealerPayload[`notif_${c}_whatsapp_enabled`] = !!form[`notif_${c}_whatsapp_enabled`];
                });
                dealerPayload.whatsapp_enabled = !!form.whatsapp_enabled;
                dealerPayload.whatsapp_number = form.whatsapp_number?.trim() || null;
                dealerPayload.direct_chat_enabled = !!form.direct_chat_enabled;
                // Jul-2026 (User-Wunsch) — Sichtbarkeits-Standard (B2C/B2B) + B2B-Händler-Allowlist.
                dealerPayload.default_visibility_b2c = form.default_visibility_b2c !== false;
                dealerPayload.default_visibility_b2b = form.default_visibility_b2b !== false;
                dealerPayload.b2b_visible_dealer_ids = Array.isArray(form.b2b_visible_dealer_ids) ? form.b2b_visible_dealer_ids : [];
                dealerPayload.auto_contract_dispatch_enabled = !!form.auto_contract_dispatch_enabled;
                if (form.auto_contract_dispatch_enabled) {
                    const rh = Number(form.reservation_hours);
                    if (!Number.isFinite(rh) || rh < 1 || rh > 720) {
                        setSavingMarket(false);
                        if (fail("Bitte eine Reservierungsdauer zwischen 1 und 720 Stunden eingeben") === false) return;
                    }
                    dealerPayload.reservation_hours = Math.round(rh);
                    dealerPayload.financing_link_url = form.financing_link_url?.trim() || null;
                } else {
                    dealerPayload.reservation_hours = null;
                    dealerPayload.financing_link_url = null;
                }
                await api.patch("/users/me/dealer-profile", dealerPayload);
            }
            await api.patch("/users/me/preferences", prefsPayload);
            await refresh();
            setLastSavedAt(Date.now());
            if (!silent) {
                setUndoSnapshot({
                    prefs: { ...currentUser },
                    form: { ...initialForm },
                });
                setUndoVisible(true);
                toast.success("Marktplatzeinstellungen gespeichert");
            }
        } catch (e) {
            if (silent) throw e;
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSavingMarket(false);
        }
    };

    // Autospeichern der Marktplatzeinstellungen (debounced, nur wenn Bereich offen).
    const sigMarket = useAutosave(form, () => saveMarket({ silent: true }), { delay: 1200, enabled: marketExpanded });

    // Jul-2026 (User-Wunsch) — „Alles ein-/ausklappen" (Buttons in der TOC-Sidebar).
    useEffect(() => {
        const h = (e) => {
            const open = Boolean(e.detail?.open);
            setMarketExpanded(open);
            setMarketingExpanded(open);
            setIntegrationsExpanded(open);
            setLayoutOpen(open);
        };
        window.addEventListener("twowheels:settings-expand-all", h);
        return () => window.removeEventListener("twowheels:settings-expand-all", h);
    }, []);

    return (
        <div className="space-y-6" data-testid="settings-page">
            <FilialeInheritanceBanner />
            {/* Phase 3.86 — Persistenter Save-Status (Notion/Linear-Style).
                Steht über dem Sticky-Banner und bleibt auch nach Save sichtbar
                ("✓ Gespeichert vor 3 s"), während der Banner nur bei Dirty-State
                erscheint. */}
            <div className="flex items-center justify-end h-5 -mb-2" data-testid="settings-save-status-row">
                <SaveStatusIndicator
                    savedAt={lastSavedAt}
                    dirty={profileDirty || dirtyMarket}
                    saving={savingMarket}
                />
            </div>

            {/* Phase 4c+++++++ — Sticky-TOC + Sections-Layout */}
            <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-6 lg:items-start">
                <SettingsToc sections={(() => {
                    const list = [];
                    if (currentUser.role === "b2b") {
                        list.push({ id: "settings-profile", label: "Stammdaten", Icon: User });
                        list.push({ id: "settings-standorte", label: "Standorte & Filialen", Icon: GitBranch });
                        list.push({ id: "settings-employees", label: "Mitarbeiter", Icon: Users });
                        list.push({ id: "settings-finanzen", label: "Finanzen / Rechtliches", Icon: CreditCard });
                        list.push({ id: "settings-boni-katalog", label: "Boni-Katalog / Hersteller / Händleraktionen / Rabatt / Gutscheine", Icon: Gift });
                        list.push({ id: "settings-warenwirtschaft", label: "Warenwirtschaft", Icon: Package });
                        list.push({ id: "settings-werkstatt", label: "Werkstatt & Teile", Icon: Wrench });
                        list.push({ id: "settings-layout-branding", label: "Layout & Branding", Icon: Palette });
                        list.push({ id: "settings-ki-wissen", label: "KI-Wissensdatenbank", Icon: BookOpenText });
                    }
                    if (currentUser.role === "b2c") {
                        list.push({ id: "settings-finanzen", label: "Finanzen / Rechtliches", Icon: CreditCard });
                    }
                    list.push({ id: "settings-market", label: "Marktplatz", Icon: ShoppingBag });
                    if (currentUser.role === "b2b") {
                        list.push({ id: "settings-marketing", label: "Marketing", Icon: Sparkles });
                        list.push({ id: "settings-integrations", label: "Import / Export / Schnittstellen", Icon: Plug });
                    }
                    return list;
                })()} onJump={(id) => {
                    if (id === "settings-market") setMarketExpanded(true);
                    if (id === "settings-marketing") setMarketingExpanded(true);
                    if (id === "settings-integrations") setIntegrationsExpanded(true);
                    if (id === "settings-koenig-import") setIntegrationsExpanded(true);
                    if (id === "settings-standorte") window.dispatchEvent(new CustomEvent("twowheels:expand-standorte"));
                }} />
                <div className="space-y-6 lg:min-w-0">

            {/* === 1) HÄNDLERPROFIL (B2B) === */}
            {currentUser.role === "b2b" && (
                <>
                    <div id="settings-profile">
                        <DealerProfileSection currentUser={currentUser} onDirtyChange={setProfileDirty} />
                    </div>
                    <div id="settings-standorte">
                        <OrgBranchSettings />
                    </div>
                    <div id="settings-employees">
                        <EmployeesSection currentUser={currentUser} />
                    </div>
                    <div id="settings-commission">
                        <OrgLockedSection area="provisionen" label="Die Provisionen-Konfiguration">
                            <CommissionConfigSection currentUser={currentUser} />
                        </OrgLockedSection>
                    </div>
                    <div id="settings-finanzen">
                        <OrgLockedSection area="vertragsverhaeltnis" label="Das Vertragsverhältnis">
                            <FinanzenSection currentUser={currentUser} />
                        </OrgLockedSection>
                    </div>
                    <div id="settings-boni-katalog">
                        <OrgLockedSection area="boni_katalog" label="Der Boni-Katalog / Hersteller- & Händleraktionen / Rabatt / Gutscheine">
                            <BonusCatalogSection currentUser={currentUser} />
                        </OrgLockedSection>
                    </div>
                    <div id="settings-ki-wissen">
                        <AssistantKnowledgeSection />
                    </div>
                </>
            )}
            {/* Warenwirtschaft + Werkstatt (mit Zahnrädern) — auch für Filial-/Verkaufsleiter im Mitarbeiter-Login */}
            {(currentUser.role === "b2b" || isBranchLead) && (
                <>
                    <div id="settings-warenwirtschaft">
                        <WarenwirtschaftSection currentUser={currentUser} />
                    </div>
                    <div id="settings-werkstatt">
                        <WerkstattTeileZubehoerSection currentUser={currentUser} />
                    </div>
                </>
            )}
            {currentUser.role === "b2b" && (
                <>
                    <div id="settings-layout-branding" className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="layout-branding-section">
                        <button
                            type="button"
                            onClick={() => setLayoutOpen((o) => !o)}
                            className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                            aria-expanded={layoutOpen}
                            data-testid="layout-branding-toggle"
                        >
                            <Palette className="w-5 h-5 text-[#FFCC00] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <h2 className="font-display text-lg font-bold">Layout &amp; Branding</h2>
                                <p className="text-xs text-gray-600 mt-1">Dokument-Kopf/-Fuß, Muster-Vorschau aller Dokumente, E-Mail-Vorlagen &amp; Markendesign.</p>
                            </div>
                            <span className={`text-gray-400 text-lg transition-transform shrink-0 ${layoutOpen ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                        </button>

                        {layoutOpen && (
                        <div className="border-t border-gray-200">
                            {/* Live-Vorschau Kopf-/Fußzeile inkl. Prüfung fehlender Pflichtangaben */}
                            <FooterPreviewCard currentUser={currentUser} />

                            {/* Dokumente testen (Sammel-PDF + Einzeldokument-Dropdown) */}
                            <DocumentSamplesCard />

                            <div id="settings-email-templates">
                            <OrgLockedSection area="email_templates" label="Die E-Mail-Vorlagen">
                                <EmailTemplatesSection currentUser={currentUser} />
                            </OrgLockedSection>
                            </div>
                            <OrgLockedSection area="branding" label="Das Markendesign">
                                <BrandingSection currentUser={currentUser} />
                            </OrgLockedSection>
                        </div>
                        )}
                    </div>
                </>
            )}
            {currentUser.role === "b2c" && (
                <>
                    <div id="settings-finanzen">
                        <FinanzenSection currentUser={currentUser} />
                    </div>
                </>
            )}

            {/* Tour & Hilfe ist ins Händler-Dashboard umgezogen (· 261, User-Wunsch). */}

            {/* === 2) MARKTPLATZEINSTELLUNGEN (unified single-save form, kollabierbar Phase 3.22) === */}
            <div id="settings-market" className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="market-settings-section">
                <div className="flex items-center border-b border-gray-200 bg-[#FFFBEB]/40">
                <button
                    type="button"
                    onClick={() => setMarketExpanded((v) => !v)}
                    className="flex-1 flex items-center gap-3 px-6 py-4 text-left min-w-0"
                    data-testid="market-settings-toggle"
                    aria-expanded={marketExpanded}
                >
                    <ShoppingBag className="w-5 h-5 text-[#FFCC00] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-lg font-bold">Marktplatzeinstellungen</h2>
                        <p className="text-xs text-gray-600 mt-1">Steuern Sie Verhandlung, Kommunikation und Verkauf in einem.</p>
                    </div>
                    <span className={`text-gray-400 text-lg transition-transform shrink-0 ${marketExpanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                </button>
                <span className="px-4 shrink-0" data-testid="market-settings-save-status">
                    <SaveStatusIndicator saving={sigMarket.saving} dirty={sigMarket.dirty} savedAt={sigMarket.savedAt} />
                </span>
                <div className="pr-5 pl-2 shrink-0">
                    <BranchOverrideToggle
                        area="marketplace"
                        currentUser={currentUser}
                        infoText="Marktplatzeinstellungen des Hauptsitzes für alle Filialen übernehmen. Über die Teilbereiche (ⓘ) können Sie einzelne Bereiche von der Übernahme ausnehmen."
                        subGroups={[
                            { key: "verhandlung", label: "Verhandlung & Angebote" },
                            { key: "kommunikation", label: "Kommunikation (Chat / WhatsApp / Anruf)" },
                            { key: "sofortkauf", label: "Sofortkauf & Vertrag" },
                            { key: "auktionen", label: "Auktionen" },
                            { key: "weiteres", label: "Inzahlungnahme · Probefahrt · Ankauf · Programme" },
                        ]}
                        testId="marketplace-branch-override"
                    />
                </div>
                </div>
                {marketExpanded && (
                <div className="px-6 pb-6" data-testid="market-settings-body">
                    <MarketplaceSettingsSection
                        currentUser={currentUser}
                        form={form}
                        updateForm={updateForm}
                        savingMarket={savingMarket}
                    />
                </div>
                )}
            </div>

            {/* B2B-Verkauf erweitert (Zielgruppen ist jetzt in MarketingSection eingebettet) */}
            {currentUser.role === "b2b" && (
                <>
                    {/* === 3) MARKETING (bündelt Börsen + Sozialmedia + Zielgruppen + Werbemittel + Homepage-Embed) === */}
                    <div id="settings-marketing" className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="marketing-section-wrapper">
                        <div className="flex items-center border-b border-gray-200 bg-[#FFFBEB]/40">
                            <button
                                type="button"
                                onClick={() => setMarketingExpanded((v) => !v)}
                                className="flex-1 flex items-center gap-3 px-6 py-4 text-left min-w-0"
                                data-testid="marketing-section-toggle"
                                aria-expanded={marketingExpanded}
                            >
                                <Sparkles className="w-5 h-5 text-[#FFCC00] shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-display text-lg font-bold">Marketing</h2>
                                    <p className="text-xs text-gray-600 mt-1">Transaktions-Code, QR, Werbemittel, Börsen-Exporte, Social Media, Zielgruppen & Händler-Homepage.</p>
                                </div>
                                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${marketingExpanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                            </button>
                            <div className="pr-5 pl-2 shrink-0">
                                <BranchOverrideToggle
                                    area="marketing"
                                    currentUser={currentUser}
                                    infoText="Marketing-Einstellungen des Hauptsitzes für alle Filialen übernehmen. Die Ein-/Aus-Schalter je Unterbereich finden Sie direkt rechts an jedem Unterbereich. „Transaktions-Code/Die Zweiradhändler“ & „Börsenexporte“ bleiben immer pro Filiale."
                                    testId="marketing-branch-override"
                                />
                            </div>
                        </div>
                        {marketingExpanded && (
                            <div data-testid="marketing-section-body">
                                <MarketingSection currentUser={currentUser} />
                            </div>
                        )}
                    </div>

                    {/* === 4) IMPORT / EXPORT / SCHNITTSTELLEN === */}
                    <div id="settings-integrations" className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="integrations-section-wrapper">
                        <button
                            type="button"
                            onClick={() => setIntegrationsExpanded((v) => !v)}
                            className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                            data-testid="integrations-section-toggle"
                            aria-expanded={integrationsExpanded}
                        >
                            <Plug className="w-5 h-5 text-[#2563EB] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <h2 className="font-display text-lg font-bold">Import / Export / Schnittstellen</h2>
                                <p className="text-xs text-gray-600 mt-1">Warenbestand-Upload, Fahrzeug-Export, Fahrzeug-Import (Datei & Automatik), Live-Bestandssync & Kundenimport.</p>
                            </div>
                            <span className={`text-gray-400 text-lg transition-transform shrink-0 ${integrationsExpanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
                        </button>
                        {integrationsExpanded && (
                            <div className="px-6 pb-6 space-y-6" data-testid="integrations-section-body">
                                <CollapsibleCard icon={Package} iconClass="text-[#FFCC00]" title="Warenbestand" hint="Fahrzeug-Warenbestand & Artikel/Teile importieren (CSV-Massen-Import)." testId="integrations-warenbestand-block">
                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Fahrzeuge (CSV-Massen-Import)</div>
                                            <UploadPanel />
                                        </div>
                                        <div className="pt-4 border-t border-gray-100">
                                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Artikel / Teile</div>
                                            <p className="text-xs text-gray-600 mb-2">CSV- &amp; König-Teilebestand importieren — Vorschau, Spalten-Mapping &amp; Commit.</p>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setArticleImportOpen(true)}
                                                className="rounded-sm border-[#111827] text-[#111827] font-bold"
                                                data-testid="integrations-article-import-open"
                                            >
                                                <Package className="w-4 h-4 mr-2" /> Artikel-Import öffnen
                                            </Button>
                                            {articleImportOpen && (
                                                <ImportModal
                                                    suppliers={[]}
                                                    onClose={() => setArticleImportOpen(false)}
                                                    onDone={() => { setArticleImportOpen(false); toast.success("Import abgeschlossen — Artikel sind in der Warenwirtschaft sichtbar."); }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </CollapsibleCard>
                                <VehicleExportSection currentUser={currentUser} />
                                {/* Sep-2026 (User-Wunsch): 3 überlappende Importe → EIN „Fahrzeug-Import"
                                    mit zwei Tabs (Datei-Abgleich + Automatik). Live-Sync bleibt separat. */}
                                <CollapsibleCard icon={Car} iconClass="text-[#FFCC00]" title="Fahrzeug-Import" hint="Bestand per Datei einspielen (Excel/CSV mit Abgleich) oder automatisch per Feed/SFTP." testId="vehicle-import-hub">
                                    <Tabs defaultValue="datei" className="w-full">
                                        <TabsList className="mb-4">
                                            <TabsTrigger value="datei" data-testid="vehicle-import-tab-datei">Datei-Abgleich</TabsTrigger>
                                            <TabsTrigger value="auto" data-testid="vehicle-import-tab-auto">Automatik (Feed/SFTP)</TabsTrigger>
                                        </TabsList>
                                        <TabsContent value="datei" id="settings-koenig-import" data-testid="vehicle-import-datei-body">
                                            <KoenigVehicleImportSection currentUser={currentUser} />
                                        </TabsContent>
                                        <TabsContent value="auto" data-testid="vehicle-import-auto-body">
                                            <VehicleImportSection currentUser={currentUser} />
                                        </TabsContent>
                                    </Tabs>
                                </CollapsibleCard>
                                <KoenigIntegrationSection currentUser={currentUser} />
                                <CustomerImportSection currentUser={currentUser} />
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* === 4) Push-Benachrichtigungen — Jun-2026 wieder aktiviert (Geräte-Verwaltung + Test) === */}
            <div id="settings-push" className="mt-4">
                <PushSettingsSection />
            </div>
                </div>
            </div>
            {/* Phase 3.86+ — Undo-Snackbar (10s) nach erfolgreichem Save */}
            <UndoSnackbar
                open={undoVisible}
                label="Marktplatzeinstellungen gespeichert"
                onUndo={performUndo}
                onTimeout={() => { setUndoVisible(false); setUndoSnapshot(null); }}
            />
        </div>
    );
}

